import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import PasswordInput from './ui/PasswordInput';
import { CrownIcon, ShieldCheckIcon, AlertTriangleIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraAdminLogin = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ultra-admin/auth/login`, {
        email: credentials.email,
        password: credentials.password
      });

      if (response.data.access_token) {
        // Store Ultra Admin token separately
        localStorage.setItem('ultra_admin_token', response.data.access_token);
        localStorage.setItem('ultra_admin_user', JSON.stringify(response.data.user));
        
        toast.success('Ultra Admin paneline hoş geldiniz!');
        navigate('/ultra-admin/dashboard');
      }
    } catch (error) {
      console.error('Ultra Admin login error:', error);
      toast.error(
        error.response?.data?.detail || 
        'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black bg-opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <Card className="w-full max-w-md relative z-10 bg-white/95 backdrop-blur-sm shadow-2xl border-0">
        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
              <CrownIcon className="w-10 h-10 text-white" />
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Ultra Admin Panel</h1>
              <p className="text-gray-600">Platform Seviyesi Yönetim Girişi</p>
            </div>

            {/* Security Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-3">
              <AlertTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-xs font-medium text-amber-800">Güvenlik Uyarısı</p>
                <p className="text-xs text-amber-700 mt-1">
                  Bu panel sadece platform yöneticileri içindir. Tüm aktiviteler kaydedilir.
                </p>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ShieldCheckIcon className="w-4 h-4 inline mr-1" />
                Ultra Admin E-posta
              </label>
              <Input
                type="email"
                name="email"
                value={credentials.email}
                onChange={handleInputChange}
                placeholder="ultra@advancecash.io"
                required
                disabled={isLoading}
                className="h-11 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              />
              <div className="text-xs text-gray-500 mt-1">
                Demo: ultra@advancecash.io
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master Şifre
              </label>
              <PasswordInput
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder="UltraAdmin2025!"
                required
                disabled={isLoading}
                className="h-11 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              />
              <div className="text-xs text-gray-500 mt-1">
                Demo: UltraAdmin2025!
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !credentials.email || !credentials.password}
              className="w-full h-11 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium shadow-lg transition-all duration-200"
            >
              {isLoading ? 'Doğrulanıyor...' : 'Ultra Admin Girişi'}
            </Button>

            {/* Demo Login Button */}
            <Button
              type="button"
              onClick={() => {
                setIsLoading(true);
                
                // Mock demo login without API call
                setTimeout(() => {
                  // Create mock token and user data
                  const mockToken = "demo_ultra_admin_token_" + Date.now();
                  const mockUser = {
                    email: "ultra@advancecash.io",
                    full_name: "Ultra Admin",
                    role: "ultra_admin"
                  };
                  
                  localStorage.setItem('ultra_admin_token', mockToken);
                  localStorage.setItem('ultra_admin_user', JSON.stringify(mockUser));
                  
                  toast.success('Demo Ultra Admin girişi başarılı!');
                  setIsLoading(false);
                  navigate('/ultra-admin/dashboard');
                }, 1000); // Simulate loading time
              }}
              disabled={isLoading}
              className="w-full h-11 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium shadow-lg transition-all duration-200"
            >
              {isLoading ? 'Demo Girişi...' : '🚀 Demo Girişi (Geçici)'}
            </Button>
          </form>

          {/* Footer */}
          <div className="text-center pt-4 border-t border-gray-200">
            <Link 
              to="/" 
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Ana Sayfaya Dön
            </Link>
          </div>

          {/* Version Info */}
          <div className="text-center">
            <p className="text-xs text-gray-400">
              Advancecash.io Ultra Admin v1.0 | Platform Management Console
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UltraAdminLogin;